import { Injectable } from '@angular/core'
import { Timestamp } from '@angular/fire/firestore'
import { NavigationStart, Router } from '@angular/router'
import Swal from 'sweetalert2'
import { AuthenticationService } from './authentication.service'
import { 
    detectEnterprise, 
    EnterpriseConfig, 
    getEnterpriseConfig, 
    isEnterpriseContext,
    ENTERPRISE_CONFIGS 
} from '../config/enterprise.config'

@Injectable({
    providedIn: 'root',
})
export class GlobalService {
    static globalSideMenuCollapsed: boolean = true
    routes: any = {}
    subdomain: string
    
    // Enterprise configuration (loaded synchronously for instant branding)
    enterpriseConfig: EnterpriseConfig
    enterpriseKey: string
    
    // Legacy whitelabel properties (maintained for backwards compatibility)
    whitelabeled: boolean
    whitelabelLogoUrl: string
    whitelabelColour: string
    whitelabelBannerUrl: string
    whitelabelBuilding: string
    whitelabelManager: string
    whitelabelHeading: string
    whitelabelMerchant: string
    whitelabelName: string
    whitelabelHeaderColour: string
    whitelabelHeaderLight = false
    
    contextMenuVisible: boolean
    contextMenuItems: any[] = []
    contextMenuX: number
    contextMenuY: number
    private contextMenuHideTimeout: any = null

    constructor(private router: Router, private authenticationService: AuthenticationService) {
        // Listen to routing events
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                GlobalService.globalSideMenuCollapsed = true
            }
        })

        this.routes.parking = `/parking/`

        // Detect enterprise synchronously for instant branding (no flash)
        this.enterpriseConfig = detectEnterprise()
        this.enterpriseKey = this.enterpriseConfig.key
        
        // Set legacy properties from enterprise config
        this.whitelabeled = !this.enterpriseConfig.isDefault
        this.whitelabelLogoUrl = this.enterpriseConfig.logoUrl
        this.whitelabelColour = this.enterpriseConfig.primaryColor
        this.whitelabelHeaderColour = this.enterpriseConfig.headerColor
        this.whitelabelHeaderLight = this.enterpriseConfig.headerLight
        this.whitelabelName = this.enterpriseConfig.key
        this.whitelabelHeading = this.enterpriseConfig.homepageHeading || this.enterpriseConfig.name
        this.whitelabelBannerUrl = this.enterpriseConfig.bannerUrl || ''
        
        // Set enterprise-specific favicon
        if (this.enterpriseConfig.faviconUrl) {
            this.setFavicon(this.enterpriseConfig.faviconUrl)
        }
        
        // Set enterprise-specific page title (base title)
        if (this.enterpriseConfig.pageTitle) {
            this.setDefaultPageTitle(this.enterpriseConfig.pageTitle)
        }
        
        // Set CSS custom properties for enterprise colors
        this.setEnterpriseColors()
        
        // Also support legacy subdomain-based whitelabels (for non-enterprise merchants)
        const domainParts = location.hostname.split('.')
        if (domainParts) {
            this.subdomain = domainParts[0]

            // Legacy merchant-level whitelabels (not enterprise-level)
            // These are kept for backwards compatibility with existing merchant whitelabels
            if (this.subdomain === 'spire') {
                this.whitelabeled = true
                this.whitelabelLogoUrl = 'assets/whitelabel/spire/logo.png'
                this.whitelabelColour = '#0da676'
                this.whitelabelHeaderColour = '#0da676'
                this.whitelabelMerchant = 'WtUfbAg95cQQSCBtnQQUBJ9O4WU2'
                this.whitelabelHeading = 'Parkings Managed By Spire'
            }

            if (this.subdomain === 'cityprop') {
                this.whitelabeled = true
                this.whitelabelLogoUrl = 'assets/whitelabel/cityprop/logo.svg'
                this.whitelabelColour = '#06223E'
                this.whitelabelHeaderColour = '#06223E'
                this.whitelabelBannerUrl = 'assets/whitelabel/cityprop/banner.png'
                this.whitelabelBuilding = 'City Property'
                this.whitelabelHeading = 'Parkings in City Property Buildings'
            }

            if (this.subdomain === 'advance') {
                this.whitelabeled = true
                this.whitelabelLogoUrl = 'assets/whitelabel/advance/logo.svg'
                this.whitelabelColour = '#650058'
                this.whitelabelHeaderColour = '#650058'
                this.whitelabelManager = 'Advance on point solutions'
                this.whitelabelHeading = 'Parkings Managed By Advance On Point Solutions'
            }

            if (this.subdomain === 'admyt') {
                this.whitelabeled = true
                this.whitelabelLogoUrl = 'assets/whitelabel/admyt/logo.png'
                this.whitelabelColour = '#FCD004'
                this.whitelabelHeaderColour = '#FCD004'
                this.whitelabelHeading = 'Parkings With Admyt Access Control'
            }

            if (this.subdomain === 'docklands') {
                this.whitelabeled = true
                this.whitelabelLogoUrl = 'assets/whitelabel/docklands/logo.svg'
                this.whitelabelColour = '#266ec2'
                this.whitelabelHeaderColour = '#266ec2'
                this.whitelabelManager = 'Docklands Ventures'
                this.whitelabelHeading = 'Driving Innovative Thinking Across Real Estate Markets'
            }
        }
    }
    
    /**
     * Check if we're in an enterprise context (not default ParkUpp)
     */
    get isEnterprise(): boolean {
        return !this.enterpriseConfig.isDefault
    }
    
    /**
     * Get the contact email for the current enterprise
     */
    get contactEmail(): string {
        return this.enterpriseConfig.contactEmail || 'info@parkupp.co.za'
    }
    
    /**
     * Get the contact phone for the current enterprise
     */
    get contactPhone(): string | undefined {
        return this.enterpriseConfig.contactPhone
    }
    
    /**
     * Check if WhatsApp button should be shown
     */
    get showWhatsAppButton(): boolean {
        return this.enterpriseConfig.showWhatsAppButton !== false // Default to true
    }
    
    /**
     * Set the favicon dynamically
     */
    private setFavicon(faviconUrl: string): void {
        const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link')
        link.type = 'image/x-icon'
        link.rel = 'shortcut icon'
        link.href = faviconUrl
        document.getElementsByTagName('head')[0].appendChild(link)
    }
    
    /**
     * Set the default page title (can be overridden by individual pages)
     */
    private setDefaultPageTitle(title: string): void {
        document.title = title
    }
    
    /**
     * Set CSS custom properties for enterprise colors and fonts
     * These can be used in SCSS with var(--enterprise-secondary) etc.
     * Also sets data-enterprise attribute on html for enterprise-specific global styles
     */
    private setEnterpriseColors(): void {
        const root = document.documentElement
        root.style.setProperty('--enterprise-secondary', this.enterpriseConfig.secondaryColor)
        root.style.setProperty('--enterprise-primary', this.enterpriseConfig.primaryColor)
        
        // Set enterprise identifier for global CSS targeting
        if (!this.enterpriseConfig.isDefault) {
            root.setAttribute('data-enterprise', this.enterpriseConfig.key)
        } else {
            root.removeAttribute('data-enterprise')
        }
    }
    
    /**
     * Get the current enterprise name for display
     */
    get enterpriseName(): string {
        return this.enterpriseConfig.name
    }
    
    /**
     * Set the enterprise context (e.g., from admin dropdown)
     */
    setEnterpriseContext(enterpriseKey: string): void {
        const config = getEnterpriseConfig(enterpriseKey)
        this.enterpriseConfig = config
        this.enterpriseKey = config.key
        
        // Update legacy properties
        this.whitelabeled = !config.isDefault
        this.whitelabelLogoUrl = config.logoUrl
        this.whitelabelColour = config.primaryColor
        this.whitelabelHeaderColour = config.headerColor
        this.whitelabelHeaderLight = config.headerLight
        this.whitelabelName = config.key
        this.whitelabelHeading = config.homepageHeading || config.name
        this.whitelabelBannerUrl = config.bannerUrl || ''
        
        // Update CSS custom properties
        this.setEnterpriseColors()
    }
    
    /**
     * Get all available enterprises (for admin dropdown)
     */
    getAllEnterprises(): EnterpriseConfig[] {
        return Object.values(ENTERPRISE_CONFIGS)
    }

    static verticalMenuClass() {
        if (GlobalService.globalSideMenuCollapsed) {
            return 'side-menu-collapsed'
        }
        return 'side-menu-expanded'
    }

    goHome() {
        // Navigate to root - enterprise vs standard ParkUpp is handled by domain detection
        window.location.href = '/'
    }

    public getCookie(name: string) {
        let ca: Array<string> = document.cookie.split(';')
        let caLen: number = ca.length
        let cookieName = `${name}=`
        let c: string

        for (let i: number = 0; i < caLen; i += 1) {
            c = ca[i].replace(/^\s+/g, '')
            if (c.indexOf(cookieName) == 0) {
                return c.substring(cookieName.length, c.length)
            }
        }
        return ''
    }

    public deleteCookie(cookieName: string) {
        this.setCookie({ name: cookieName, value: '', expireDays: -1 })
    }

    /**
     * Expires default 1 day
     * If params.session is set and true expires is not added
     * If params.path is not set or value is not greater than 0 its default value will be root "/"
     * Secure flag can be activated only with https implemented
     * Examples of usage:
     * {service instance}.setCookie({name:'token',value:'abcd12345', session:true }); <- This cookie will not expire
     * {service instance}.setCookie({name:'userName',value:'John Doe', secure:true }); <- If page is not https then secure will not apply
     * {service instance}.setCookie({name:'niceCar', value:'red', expireDays:10 }); <- For all this examples if path is not provided default will be root
     */
    public setCookie(params: any) {
        let d: Date = new Date()
        d.setTime(d.getTime() + (params.expireDays ? params.expireDays : 1) * 24 * 60 * 60 * 1000)
        document.cookie =
            (params.name ? params.name : '') +
            '=' +
            (params.value ? params.value : '') +
            ';' +
            (params.session && params.session == true ? '' : 'expires=' + d.toUTCString() + ';') +
            'path=' +
            (params.path && params.path.length > 0 ? params.path : '/') +
            ';' +
            (location.protocol === 'https:' && params.secure && params.secure == true ? 'secure' : '')
    }

    addScript(source: string, id: string) {
        const newElement = document.createElement('script'),
            existingElement = document.getElementsByTagName('script')[0]
        newElement.async = true
        newElement.src = source
        newElement.id = id
        existingElement.parentNode?.insertBefore(newElement, existingElement)
    }

    exportcsv(tableId: string): void {
        let csv = ''
        const table = document.getElementById(tableId)
        const tr = table!.children[0].children[0] as any

        // Helper to escape CSV values - wrap in quotes and escape internal quotes
        const escapeCsvValue = (value: string): string => {
            // Replace newlines and carriage returns with spaces
            let cleaned = value.replace(/[\r\n]+/g, ' ').trim()
            // If value contains comma, quote, or spaces that could cause issues, wrap in quotes
            if (cleaned.includes(',') || cleaned.includes('"') || cleaned.includes('\t')) {
                // Escape any double quotes by doubling them
                cleaned = '"' + cleaned.replace(/"/g, '""') + '"'
            }
            return cleaned
        }

        // Helper to get clean text from a cell (first text content, ignoring buttons/icons)
        const getCellText = (cell: HTMLElement): string => {
            // Try to find a span or direct text content first
            const textSpan = cell.querySelector('.contact-text, .client-name, .company-name, .notes-text, .badge, span:not([class*="btn"])')
            if (textSpan) {
                return textSpan.textContent?.trim() || ''
            }
            // For cells without special structure, get innerText but clean it
            const text = cell.innerText || ''
            return text.trim()
        }

        if (tr) {
            for (let i = 0; i < tr.children.length; i++) {
                const th = tr.children[i]
                const exclude = th.getAttribute('exclude')
                if (exclude !== 'true') {
                    csv += escapeCsvValue(th.innerText) + ','
                }
            }
            csv = csv.substring(0, csv.length - 1) + '\n'

            const tbody = table!.children[1] as any
            for (let i = 0; i < tbody.children.length; i++) {
                for (let j = 0; j < tbody.children[i].children.length; j++) {
                    const td = tbody.children[i].children[j]
                    if (tr.children[j]) {
                        const exclude = tr.children[j].getAttribute('exclude')
                        if (exclude !== 'true') {
                            csv += escapeCsvValue(getCellText(td)) + ','
                        }
                    }
                }
                csv = csv.substring(0, csv.length - 1) + '\n'
            }

            const date = new Date().toLocaleDateString('en-CA')
            const hiddenElement = document.createElement('a')
            hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
            hiddenElement.target = '_blank'
            hiddenElement.download = `${date} ParkUpp Export ${tableId}.csv`
            hiddenElement.click()
        }
    }

    toggleSideMenu() {
        GlobalService.globalSideMenuCollapsed = !GlobalService.globalSideMenuCollapsed
    }

    convertImageMapToJSON(imageMapHTML: string, displayWidth?: number, displayHeight?: number) {
        // Only support new JSON polygon format - no more HTML image map support
        if (!imageMapHTML || !imageMapHTML.trim().startsWith('{')) {
            return []
        }

        try {
            const jsonData = JSON.parse(imageMapHTML)

            if (!jsonData.spots) {
                return []
            }

            // Simple ratio-based scaling for SVG display
            const originalWidth = jsonData.originalImageWidth || 1
            const originalHeight = jsonData.originalImageHeight || 1

            // If display dimensions provided, calculate scale ratios
            const scaleX = displayWidth ? displayWidth / originalWidth : 1
            const scaleY = displayHeight ? displayHeight / originalHeight : 1

            return jsonData.spots.map((spot: any) => {
                // Scale each polygon point for SVG display
                const scaledPolygon = spot.polygon.map((p: any) => ({
                    x: p.x * scaleX,
                    y: p.y * scaleY,
                }))

                return {
                    bayNumber: spot.name,
                    x: Math.min(...scaledPolygon.map((p: any) => p.x)).toFixed(2),
                    y: Math.min(...scaledPolygon.map((p: any) => p.y)).toFixed(2),
                    width: (Math.max(...scaledPolygon.map((p: any) => p.x)) - Math.min(...scaledPolygon.map((p: any) => p.x))).toFixed(2),
                    height: (Math.max(...scaledPolygon.map((p: any) => p.y)) - Math.min(...scaledPolygon.map((p: any) => p.y))).toFixed(2),
                    polygon: scaledPolygon,
                    id: spot.id,
                    available: spot.available !== false,
                }
            })
        } catch (error) {
            console.error('Error parsing parking layout JSON:', error)
            return []
        }
    }

    serialize(object: any): any {
        const clone = { ...object } // Shallow copy of the object

        // Iterate over the properties of the object
        for (const key in clone) {
            // Remove properties that start with '$'
            if (clone.hasOwnProperty(key) && key.startsWith('$')) {
                delete clone[key]
                continue
            }

            // Check if the property is a Firestore Timestamp and handle it appropriately
            if (clone[key] instanceof Timestamp) {
                clone[key] = object[key]
                continue
            }

            // Check if the property is empty or undefined
            if (clone[key] === undefined) {
                delete clone[key]
                continue
            }

            // Preserve properties that end with 'ref' or 'refs' and are not empty
            if (key.endsWith('ref') || key.endsWith('refs') || (object[key] !== undefined && object[key] !== null && object[key] !== '')) {
                clone[key] = object[key]
            }
        }

        return clone
    }

    async moreInfoMerchantPortfolioConsolidation() {
        await Swal.fire({
            title: 'More Info',
            text: 'The Portfolio Consolidation feature on ParkUpp allows you to organize various businesses or groups of buildings into separate portfolios. This functionality enables you to categorize payout information, buildings, tenants, and other relevant details according to different business entities. While the use of separate portfolios is optional, it is particularly beneficial if you require different managers to oversee specific aspects of your profile. Utilize this feature as needed to streamline your management process.',
        })
    }

    async genericInfoModal(text: string) {
        await Swal.fire({ title: 'More Info', text })
    }

    showContextMenu(event?: MouseEvent) {
        // Clear any pending hide timeout
        if (this.contextMenuHideTimeout) {
            clearTimeout(this.contextMenuHideTimeout)
            this.contextMenuHideTimeout = null
        }

        this.contextMenuVisible = true
        if (event) {
            // Use clientX/clientY for position: fixed elements (viewport-relative)
            let x = event.clientX
            let y = event.clientY

            // Approximate menu dimensions for bounds checking
            const menuWidth = 200
            const menuHeight = 250

            // Adjust position if menu would overflow viewport
            if (x + menuWidth > window.innerWidth) {
                x = window.innerWidth - menuWidth - 10
            }
            if (y + menuHeight > window.innerHeight) {
                y = window.innerHeight - menuHeight - 10
            }

            this.contextMenuX = Math.max(10, x)
            this.contextMenuY = Math.max(10, y)
            event.preventDefault()
        }
    }

    hideContextMenu() {
        this.contextMenuVisible = false
        // Clear any pending timeout
        if (this.contextMenuHideTimeout) {
            clearTimeout(this.contextMenuHideTimeout)
            this.contextMenuHideTimeout = null
        }
    }

    // Delayed hide - useful for allowing mouse to move to submenus
    hideContextMenuDelayed(delay: number = 150) {
        if (this.contextMenuHideTimeout) {
            clearTimeout(this.contextMenuHideTimeout)
        }
        this.contextMenuHideTimeout = setTimeout(() => {
            this.contextMenuVisible = false
            this.contextMenuHideTimeout = null
        }, delay)
    }

    // Cancel a pending delayed hide (call when mouse enters menu again)
    cancelContextMenuHide() {
        if (this.contextMenuHideTimeout) {
            clearTimeout(this.contextMenuHideTimeout)
            this.contextMenuHideTimeout = null
        }
    }

    async redirectToRegister() {
        await this.router.navigate([`/sign-up`], {
            queryParams: { returnUrl: this.router.url },
        })
        return
    }

    static async getPublicIP(): Promise<{ ip: string }> {
        // IP lookup disabled for now - will revisit later
        return { ip: '' }
    }

    static stripHtml(html: string) {
        return html.replace(/<[^>]*>?/g, '')
    }

    static downloadContactInfo() {
        const contactInfo = {
            name: 'ParkUpp',
            email: 'info@parkupp.co.za',
            website: 'https://parkupp.co.za',
        }

        const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${contactInfo.name}
EMAIL:${contactInfo.email}
URL:${contactInfo.website}
END:VCARD`

        const blob = new Blob([vCard], { type: 'text/vcard' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'parkupp-contact.vcf'
        link.click()
        window.URL.revokeObjectURL(url)
    }
}
